import { memo } from "react";
import { Check, Pencil, X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { HEADER_LABEL, HEADER_PANE } from "./headerStyles";

/** What a decision came to. "pending": an underwriting assessment exists but decides nothing. */
export type DecisionOutcome = "approved" | "declined" | "funded" | "pending";

/** The two decisions: underwriting (Approve / Decline) and funding (Fund / Decline). */
export type DecisionKind = "underwriting" | "funding";

const KINDS: Record<DecisionKind, { title: string; approve: string; editLabel: string }> = {
  underwriting: { title: "Decision", approve: "Approve", editLabel: "Edit decision" },
  funding: { title: "Final Decision", approve: "Fund", editLabel: "Edit final decision" },
};

const OUTCOMES: Record<DecisionOutcome, { label: string; className: string; icon?: typeof Check }> =
  {
    approved: { label: "Approved", className: "bg-green-600 text-white", icon: Check },
    funded: { label: "Funded", className: "bg-green-600 text-white", icon: Check },
    declined: {
      label: "Declined",
      className: "bg-destructive text-destructive-foreground",
      icon: X,
    },
    pending: { label: "Pending", className: "bg-muted text-foreground" },
  };

/** A button in place of the column's own pair (a chat card's choice options). */
export interface DecisionAction {
  label: string;
  /** "approve": green; "decline": destructive; "quiet": outline (a "Don't ..." option). */
  tone: "approve" | "decline" | "quiet";
  onClick: () => void;
  disabled?: boolean;
}

const ACTION_TONES: Record<
  DecisionAction["tone"],
  { variant: "default" | "destructive" | "outline"; className?: string; icon?: typeof Check }
> = {
  approve: { variant: "default", className: "bg-green-600 hover:bg-green-600/90", icon: Check },
  decline: { variant: "destructive", icon: X },
  quiet: { variant: "outline" },
};

export interface DecisionColumnProps {
  kind: DecisionKind;
  /** The decision so far; null while undecided. */
  outcome: DecisionOutcome | null;
  /** Whether this user may decide; otherwise an undecided column says so. */
  canAct: boolean;
  /** Funding: the deal is not in Final Underwriting yet, so there is nothing to decide. */
  awaiting?: boolean;
  onApprove?: () => void;
  onDecline?: () => void;
  /** Shows the pencil on a decided column. */
  onEdit?: () => void;
  approveDisabled?: boolean;
  /** While undecided, these buttons instead of Approve (Fund) / Decline. */
  actions?: DecisionAction[];
  /** An undecided column's text when there is nothing to press (default "No decision yet"). */
  idle?: string;
  /** Both buttons wait (the decision is being saved). */
  busy?: boolean;
  error?: string | null;
  /** Why a button is off, under them (quieter than `error`): a chat card's invalid terms. */
  note?: string | null;
  /**
   * "stacked" (default): the buttons one above the other at any width (the dashboard's header).
   * "auto": side by side once the column's own width allows (16rem), the error still below
   * them; needs a parent whose width does not come from its content.
   */
  layout?: "stacked" | "auto";
  className?: string;
}

const IDLE_BLOCK =
  "flex flex-1 cursor-default select-none items-center justify-center rounded-md bg-muted px-4 text-center text-xs font-medium text-muted-foreground";

function Body({
  kind,
  outcome,
  canAct,
  awaiting,
  onApprove,
  onDecline,
  approveDisabled,
  actions,
  idle,
  busy,
}: DecisionColumnProps) {
  if (outcome) {
    const { label, className, icon: Icon } = OUTCOMES[outcome];
    return (
      <span
        className={cn(
          "flex flex-1 cursor-default select-none items-center justify-center gap-2 rounded-md px-4 text-sm font-bold",
          className
        )}
      >
        {Icon && <Icon className="h-4 w-4" />}
        {label}
      </span>
    );
  }
  if (!canAct) return <span className={IDLE_BLOCK}>{idle ?? "No decision yet"}</span>;
  if (awaiting) return <span className={IDLE_BLOCK}>Awaiting Final Underwriting</span>;
  if (actions) {
    return (
      <>
        {actions.map(({ label, tone, onClick, disabled }) => {
          const { variant, className, icon: Icon } = ACTION_TONES[tone];
          return (
            <Button
              key={label}
              type="button"
              size="sm"
              variant={variant}
              className={cn("flex-1", className)}
              onClick={onClick}
              disabled={busy || disabled}
            >
              {Icon && <Icon className="h-3.5 w-3.5" />}
              {label}
            </Button>
          );
        })}
      </>
    );
  }
  return (
    <>
      <Button
        type="button"
        size="sm"
        className="flex-1 bg-green-600 hover:bg-green-600/90"
        onClick={onApprove}
        disabled={busy || approveDisabled}
      >
        <Check className="h-3.5 w-3.5" />
        {KINDS[kind].approve}
      </Button>
      <Button
        type="button"
        variant="destructive"
        size="sm"
        className="flex-1"
        onClick={onDecline}
        disabled={busy}
      >
        <X className="h-3.5 w-3.5" />
        Decline
      </Button>
    </>
  );
}

/**
 * The underwriting header's decision pane: its title, then the decision (a status block with a
 * pencil to change it) or the buttons to make it, from plain values.
 */
export const DecisionColumn = memo(function DecisionColumn(props: DecisionColumnProps) {
  const { kind, outcome, onEdit, error, note, layout = "stacked", className } = props;
  const pane = (
    <div className={cn(HEADER_PANE, className)}>
      <p className={cn(HEADER_LABEL, "whitespace-nowrap pr-5")}>{KINDS[kind].title}</p>
      {outcome && onEdit && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1.5 top-1.5 h-6 w-6 text-muted-foreground hover:text-foreground"
          onClick={onEdit}
          aria-label={KINDS[kind].editLabel}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      )}
      <div className="mt-2 flex flex-1 flex-col items-stretch justify-center gap-2 [&>button]:flex-none">
        {layout === "auto" ? (
          <div className="flex flex-1 flex-col items-stretch justify-center gap-2 [&>button]:flex-none [@container_decision_(min-width:16rem)]:flex-row [@container_decision_(min-width:16rem)]:[&>button]:flex-1">
            <Body {...props} />
          </div>
        ) : (
          <Body {...props} />
        )}
        {note && !outcome && <p className="text-[0.7rem] leading-snug text-destructive">{note}</p>}
        {error && (
          <Alert variant="destructive" className="py-2">
            <AlertDescription className="text-[0.7rem] leading-snug">{error}</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
  return layout === "auto" ? <div className="[container:decision/inline-size]">{pane}</div> : pane;
});
